import { Injectable, OnDestroy } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import momentDe from 'moment/locale/de';
import momentEn from 'moment/locale/en-gb';
import momentEs from 'moment/locale/es';
import momentFr from 'moment/locale/fr';
import momentIt from 'moment/locale/it';
import momentPl from 'moment/locale/pl';
import { PartialObserver, Subject, Subscription } from 'rxjs';

import { LANGUAGE_OPTIONS, LANGUAGE_REGION_OPTIONS } from '../models/list';
import { User } from '../models/user';

import { ConnectionService } from './connection.service';

const LANGUAGE = 'copo-language';

@Injectable({ providedIn: 'root' })
export class TranslationService implements OnDestroy {
	static ISO_LANG(lang?: keyof typeof LANGUAGE_OPTIONS | 'sp') {
		if (!lang) {
			return 'en-GB';
		}
		if (lang === 'sp') {
			return 'es-ES';
		}
		if (!(lang in LANGUAGE_REGION_OPTIONS)) {
			return 'en-GB';
		}
		return LANGUAGE_REGION_OPTIONS[lang];
	}

	currentLanguage?: keyof typeof LANGUAGE_OPTIONS;
	translateSub?: Subscription;
	private readonly languageChangeSubject = new Subject<string>();

	constructor(
		readonly translate: TranslateService,
		private readonly connectionService: ConnectionService,
		private readonly dateAdapter: DateAdapter<Date>
	) {
		console.info(`moment loaded: ${
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			momentEn ? 'en-gb' : ''
		} ${
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			momentFr ? 'fr' : ''
		} ${
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			momentIt ? 'it' : ''
		} ${
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			momentDe ? 'de' : ''
		} ${
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			momentEs ? 'es' : ''
		} ${
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			momentPl ? 'pl' : ''
		}`);
		connectionService.connectedTasks.push(this.connectedTask);
		connectionService.disconnectedTasks.push(this.disconnectedTask);

		this.translate.setDefaultLang('en');

		// migrate old language setting, to remove on next release
		const oldLanguage = localStorage.getItem('language');
		if (oldLanguage) {
			localStorage.setItem(LANGUAGE, oldLanguage);
			localStorage.removeItem('language');
		}

		const lang = localStorage.getItem(LANGUAGE) || this.translate.getBrowserLang() || 'en';
		this.setCurrentLanguage(lang);
	}

	// eslint-disable-next-line @typescript-eslint/require-await
	connectedTask = async (user: User) => {
		this.setCurrentLanguage(user.language);
	};

	// eslint-disable-next-line @typescript-eslint/require-await
	disconnectedTask = async () => {
		localStorage.removeItem(LANGUAGE);
	};

	ngOnDestroy() {
		const conIdx = this.connectionService.connectedTasks.indexOf(this.connectedTask);
		this.connectionService.connectedTasks.splice(conIdx, 1);
		const disconIdx = this.connectionService.disconnectedTasks.indexOf(this.disconnectedTask);
		this.connectionService.disconnectedTasks.splice(disconIdx, 1);
		if (this.translateSub) {
			this.translateSub.unsubscribe();
		}
	}

	getMomentLang(lang: string) {
		if (lang === 'en' && moment.locale() !== 'en-gb') {
			return 'en-gb';
		}
		if (lang === 'fr' && moment.locale() !== 'fr') {
			return 'fr';
		}
		if (lang === 'de' && moment.locale() !== 'de') {
			return 'de';
		}
		if (lang === 'es' && moment.locale() !== 'es') {
			return 'es';
		}
		if (lang === 'it' && moment.locale() !== 'it') {
			return 'it';
		}
		if (lang === 'pl' && moment.locale() !== 'pl') {
			return 'pl';
		}
		return 'en-gb';
	}

	setCurrentLanguage(lang: string) {
		const resolvedLang = this.resolveLang(lang);
		if (this.currentLanguage === resolvedLang) {
			return;
		}
		document.documentElement.lang = resolvedLang;
		this.currentLanguage = resolvedLang;
		const momentLang = this.getMomentLang(resolvedLang);
		moment.locale(momentLang);
		this.dateAdapter.setLocale(momentLang);
		localStorage.setItem(LANGUAGE, resolvedLang);
		this.connectionService.language = this.getLangWithRegion();
		if (this.translateSub) {
			this.translateSub.unsubscribe();
		}
		this.translateSub = this.translate.use(resolvedLang).subscribe({
			next() {
				console.info('language changed', resolvedLang);
			}
		});
		this.languageChangeSubject.next(this.currentLanguage);
	}

	resolveLang(lang: string) {
		if (lang in LANGUAGE_OPTIONS) {
			return <keyof typeof LANGUAGE_OPTIONS> lang;
		} else if (lang === 'sp') {
			return 'es';
		}
		return 'en';
	}

	getLangWithRegion() {
		return TranslationService.ISO_LANG(this.currentLanguage);
	}

	getTR(transid: string, params?: Record<string, unknown>) {
		return <string> this.translate.instant(transid, params);
	}

	subscribeToLanguageChange(observer: PartialObserver<string | undefined>) {
		return this.languageChangeSubject.subscribe(observer);
	}

	getIssueOriginTr(type: string) {
		if (type === 'STA') {
			return this.getTR('issue.origin.sta');
		} else if (type === 'ACH') {
			return this.getTR('issue.origin.ach');
		}
		return '';
	}
}
